/** "Pass the machine to Blue."
 *
 * The screen that makes hotseat a game rather than a way of showing one player
 * another player's map. It covers everything, it names who is next, and it says
 * nothing else about the position - no resources, no day, no hero list -
 * because whoever is looking at it right now is the player who has just
 * finished, and the point of the screen is that he stops seeing things.
 *
 * A leaf: it is handed a colour and a callback.
 *
 * ## Why it looks like a screen rather than a dialog
 *
 * It shipped as a 560px column of centred text on the shell's bare stone, and
 * nobody saw it for two rounds because the screenshot harness's `hotseat` scene
 * built a two-person game and then photographed the adventure map: `begin`
 * seats the first player itself, so the first turn owes no hand-off and the
 * scene never reached this screen at all. The two shots differed in a third of
 * a per cent of their pixels. The moment the scene was fixed, this was the
 * emptiest frame in the build.
 *
 * The single fact this screen is given is a player colour, so the colour is
 * what it is built out of: the ground is washed with it, the banner is cut from
 * it, and the seat strip says where that colour sits in the order. Everything
 * else on the screen is chrome the rest of the build already speaks - a
 * painting behind glass, struck numerals in cut recesses, a gilt plate - so it
 * reads as the same product as the screen it is covering.
 */

import { usePolishStyles } from "./polish.js";
import { ScreenStyles } from "./primitives.js";
import { ScreenBackdrop } from "./MainMenu.js";
import { titleCase } from "./format.js";
import { playerColour } from "../theme/tokens.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

export interface HandOffProps {
  /** `PlayerState.colour` of the player about to take the machine. */
  readonly colour: string;
  /** Which day is beginning, for a player who has been away from it. */
  readonly day: number;
  readonly week: number;
  readonly month: number;
  /** Seats in this game, so a player can see where he is in the order. */
  readonly seats?: readonly string[] | undefined;
  readonly onReady: () => void;
}

/** One struck numeral in a cut recess. The date is three of these, because a
 *  player who has been away from the game reads it as three separate facts. */
function DateStone(props: { readonly word: string; readonly value: number }): JSX.Element {
  return (
    <span className="hm-ho__stone">
      <span className="hm-ho__stonenum">{String(props.value)}</span>
      <span className="hm-ho__stoneword">{props.word}</span>
    </span>
  );
}

export function HandOff(props: HandOffProps): JSX.Element {
  usePolishStyles();
  useHandOffStyles();
  const name = titleCase(props.colour);
  const ink = playerColour(props.colour);
  const seats = props.seats ?? [];

  return (
    <div
      className="hs-screen hm-ho"
      role="region"
      aria-label="Pass the machine"
      style={{ ["--ho-ink" as string]: ink }}
    >
      <ScreenStyles />
      {/* A painting rather than the shell's stone ramp. It is pushed almost to
          black and then washed with the seat's own colour, so Red's hand-off
          and Blue's are two different screens at a glance from across a room -
          which is the distance this screen is actually read from. */}
      <ScreenBackdrop />
      <span className="hm-ho__wash" aria-hidden="true" />
      <span className="hm-ho__grain" aria-hidden="true" />

      <div className="hs-screen__body hm-ho__body">
        <div className="hm-ho__plate">
          {/* The banner is the player's colour cut as heraldry rather than
              printed as a swatch: a gilt-framed field with a swallow-tail foot
              and a highlight down the hoist. */}
          <span className="hm-ho__banner" aria-hidden="true">
            <span className="hm-ho__bannerfield" />
          </span>

          <div className="hm-ho__name">{`${name}'s turn`}</div>
          <div className="hm-ho__rule" aria-hidden="true" />

          <div className="hm-ho__date">
            <DateStone word="Month" value={props.month} />
            <DateStone word="Week" value={props.week} />
            <DateStone word="Day" value={props.day} />
          </div>

          <p className="hm-ho__warn">
            {"Pass the machine before you press this. What is behind it is "}
            <span className="hm-ho__warnname">{`${name}'s`}</span>
            {" to see and nobody else's."}
          </p>

          {seats.length > 1 ? (
            <div className="hm-ho__seats" aria-label="Seats">
              {seats.map((c) => (
                <span
                  key={c}
                  className={`hm-ho__seat ${c === props.colour ? "hm-ho__seat--on" : ""}`}
                  title={titleCase(c)}
                >
                  <span
                    className="hm-ho__seatchip"
                    style={{ background: playerColour(c) }}
                    aria-hidden="true"
                  />
                  <span className="hm-ho__seatname">{titleCase(c)}</span>
                </span>
              ))}
            </div>
          ) : null}

          <button
            type="button"
            className="hs-btn hs-btn--gold hm-ho__go"
            autoFocus
            onClick={props.onReady}
          >
            {`I am ${name}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ styles

const HANDOFF_STYLE_ID = "hm-handoff-art";

/** Exported so `handoff.spec.ts` can assert the geometry.
 *  NOTE: a template literal. No backticks below, in a comment or anywhere. */
export const HANDOFF_CSS = `
/* The seat's colour, threaded from the component as a custom property so every
   rule below can be written once rather than eight times. */
.hs-screen.hm-ho { background-color: #0a0705; }
.hm-ho .hm-bd__art { filter: brightness(0.60) saturate(0.58) contrast(1.10); }
/* The wash is the whole reason this screen is not grey: a broad radial of the
   seat's colour off the top, and a deep vignette under it so the plate in the
   middle is the only lit thing in the frame. */
.hm-ho__wash {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background-image:
    radial-gradient(52% 44% at 50% 46%, color-mix(in srgb, var(--ho-ink) 46%, transparent) 0%,
      rgba(0,0,0,0) 72%),
    radial-gradient(88% 62% at 50% 8%, color-mix(in srgb, var(--ho-ink) 26%, transparent) 0%,
      rgba(0,0,0,0) 66%),
    /* A vignette, not a curtain. At 0.72/0.92 the outer third of the frame was
       flat near-black: the painting behind it stopped being a painting, and a
       coarse blank-area scan read 43% of the screen as dead. It is a cover
       screen, so it stays quiet - but quiet is a landscape at dusk, not an
       unlit rectangle with a plate on it. */
    radial-gradient(124% 100% at 50% 44%, rgba(0,0,0,0) 22%, rgba(6,4,2,0.34) 66%,
      rgba(4,3,2,0.62) 100%);
}
/* The same weave the rest of the build puts over a smooth surface: a wash on
   its own is the one thing that measures as empty however good it looks. */
.hm-ho__grain {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background-image:
    repeating-linear-gradient(114deg,
      rgba(255,226,182,0.045) 0 2px,
      rgba(0,0,0,0.10) 2px 7px),
    repeating-linear-gradient(24deg,
      rgba(255,232,190,0.030) 0 1px,
      rgba(0,0,0,0.06) 1px 11px);
}
.hm-ho__body {
  z-index: 2;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
/* One plate, and it is a plate rather than loose type: the words sit on carved
   wood inside a gilt keyline, which is what every other panel in this build
   does with a sentence a player has to act on. */
.hm-ho__plate {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: min(680px, 92vw);
  padding: 30px 40px 34px;
  text-align: center;
  background-color: rgba(12,9,5,0.86);
  background-image:
    radial-gradient(92% 68% at 50% 0%, rgba(255,226,176,0.13) 0%, rgba(0,0,0,0) 66%),
    repeating-linear-gradient(97deg,
      rgba(255,232,190,0.05) 0 1px,
      rgba(0,0,0,0.10) 1px 2px,
      rgba(0,0,0,0) 2px 6px),
    linear-gradient(178deg, rgba(104,76,42,0.62) 0%, rgba(44,31,16,0.72) 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.92),
    inset 0 0 0 3px rgba(216,178,96,0.72),
    inset 0 0 0 4px rgba(0,0,0,0.85),
    inset 0 2px 0 rgba(255,232,186,0.14),
    inset 0 0 60px rgba(0,0,0,0.34),
    0 0 0 1px rgba(0,0,0,0.9),
    0 0 46px color-mix(in srgb, var(--ho-ink) 40%, transparent),
    0 18px 50px rgba(0,0,0,0.72);
}

/* ---- the banner */
.hm-ho__banner {
  flex: 0 0 auto;
  position: relative;
  width: 96px;
  height: 128px;
  filter: drop-shadow(0 8px 16px rgba(0,0,0,0.75));
}
.hm-ho__bannerfield {
  position: absolute;
  inset: 0;
  background-color: var(--ho-ink);
  background-image:
    linear-gradient(100deg,
      rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.06) 26%,
      rgba(0,0,0,0.10) 62%, rgba(0,0,0,0.42) 100%),
    repeating-linear-gradient(180deg,
      rgba(255,255,255,0.05) 0 3px, rgba(0,0,0,0.07) 3px 6px);
  box-shadow:
    inset 0 0 0 2px rgba(0,0,0,0.75),
    inset 0 0 0 4px rgba(226,186,104,0.62),
    inset 0 0 26px rgba(0,0,0,0.45);
  /* Swallow-tail: the foot is cut, so it is a banner and not a swatch. */
  clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%);
}

/* ---- the name */
.hm-ho__name {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: clamp(28px, 3.1vw, 46px);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #f7e6bb;
  text-shadow:
    0 2px 0 #241806,
    0 4px 14px rgba(0,0,0,0.9),
    0 0 26px color-mix(in srgb, var(--ho-ink) 55%, transparent);
}
.hm-ho__rule {
  width: min(420px, 78%);
  height: 3px;
  background-image: linear-gradient(90deg,
    rgba(226,186,104,0) 0%, rgba(226,186,104,0.62) 50%, rgba(226,186,104,0) 100%);
}

/* ---- the date, as three struck numerals */
.hm-ho__date { display: flex; gap: 10px; }
.hm-ho__stone {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 82px;
  padding: 6px 10px 7px;
  background-image:
    linear-gradient(180deg, rgba(0,0,0,0.44) 0%, rgba(0,0,0,0.14) 55%, rgba(0,0,0,0.40) 100%);
  box-shadow:
    inset 0 1px 0 rgba(0,0,0,0.8),
    inset 0 -1px 0 rgba(255,224,170,0.10),
    inset 0 0 0 1px rgba(0,0,0,0.55);
}
.hm-ho__stonenum {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: 26px;
  line-height: 1.06;
  color: #ecc87f;
  text-shadow: 0 2px 0 rgba(0,0,0,0.9);
}
.hm-ho__stoneword {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(9px, var(--hm-type-floor));
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #a08b62;
}

/* ---- the warning */
.hm-ho__warn {
  max-width: 46ch;
  margin: 0;
  font-size: max(13px, var(--hm-type-floor));
  line-height: 1.6;
  color: #d3bc92;
  text-shadow: 0 1px 0 rgba(0,0,0,0.85);
}
.hm-ho__warnname { color: #f3ddab; }

/* ---- the seat strip: where this colour sits in the order */
.hm-ho__seats { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
.hm-ho__seat {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 4px 10px 5px;
  background-image: linear-gradient(180deg, rgba(255,226,176,0.05), rgba(0,0,0,0.22));
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.72), inset 0 1px 0 rgba(255,226,176,0.07);
  opacity: 0.62;
}
.hm-ho__seat--on {
  opacity: 1;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.85), inset 0 0 0 2px rgba(226,186,104,0.72);
}
.hm-ho__seatchip {
  width: 13px;
  height: 13px;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.28);
}
.hm-ho__seatname {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(10px, var(--hm-type-floor));
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #c9b58c;
}
.hm-ho__go {
  margin-top: 4px;
  padding: 12px 56px;
  font-size: max(16px, var(--hm-type-floor));
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

/* 1366x768: the plate keeps every part and gives up the banner's height. */
@media (max-height: 880px) {
  .hm-ho__plate { gap: 11px; padding: 22px 32px 26px; }
  .hm-ho__banner { width: 78px; height: 100px; }
  .hm-ho__stone { min-width: 72px; }
  .hm-ho__stonenum { font-size: max(22px, var(--hm-type-floor)); }
  .hm-ho__go { padding: 9px 32px; font-size: max(14px, var(--hm-type-floor)); }
}
`;

function useHandOffStyles(): void {
  useStyleSheet(HANDOFF_STYLE_ID, HANDOFF_CSS, StyleLayer.screen);
}
